import React, { useState, useEffect, useContext } from 'react';
import { Button } from '@/components/ui/button';
import { Shield, Check, School, ChevronDown, Save, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AuthContext from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = {
  'K-5': { bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800' },
  'K-8': { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' },
  '6-8': { bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800' },
  '9-12': { bg: 'bg-purple-50', border: 'border-purple-400', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-800' },
  'all': { bg: 'bg-indigo-50', border: 'border-indigo-400', text: 'text-indigo-700', badge: 'bg-indigo-100 text-indigo-800' },
};

const LicenseManager = () => {
  const { user } = useContext(AuthContext);
  const [schools, setSchools] = useState([]);
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedSchool, setExpandedSchool] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const districtId = user?.district_id;
      const [licensesRes, tiersRes] = await Promise.all([
        axios.get(`${API}/districts/${districtId}/licenses`),
        axios.get(`${API}/license-tiers`),
      ]);
      setSchools(licensesRes.data.schools || []);
      setTiers(tiersRes.data.tiers || {});
    } catch (err) {
      toast.error('Failed to load license data');
    } finally {
      setLoading(false);
    }
  };

  const setSchoolTier = (schoolId, tier) => {
    setPendingChanges(prev => ({ ...prev, [schoolId]: tier }));
  };

  const getCurrentTier = (school) => {
    return pendingChanges[school.school_id] || school.license_tier || 'all';
  };

  const saveBulk = async () => {
    if (Object.keys(pendingChanges).length === 0) { toast.info('No changes to save'); return; }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/districts/${user?.district_id}/licenses/bulk`,
        { updates: pendingChanges },
        
      );
      toast.success(`Updated licenses for ${Object.keys(pendingChanges).length} school(s)`);
      setPendingChanges({});
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = Object.keys(pendingChanges).length > 0;

  if (loading) {
    return <div className="flex items-center justify-center py-16"><div className="loading-spinner"></div></div>;
  }

  return (
    <div data-testid="license-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Shield size={22} className="text-[#002FA7]" />
            Grade-Level Licenses
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Control which grade-level assessments each school can access based on their purchased package.
          </p>
        </div>
        {hasChanges && (
          <Button
            onClick={saveBulk}
            disabled={saving}
            className="btn-sharp-primary h-10 px-6"
            data-testid="save-licenses-btn"
          >
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : `Save ${Object.keys(pendingChanges).length} Change(s)`}
          </Button>
        )}
      </div>

      {/* Tier Legend */}
      <div className="mb-6 p-4 bg-gray-50 border border-gray-200" data-testid="tier-legend">
        <div className="flex items-center gap-2 mb-3">
          <Info size={14} className="text-gray-500" />
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Available Packages</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {Object.entries(tiers).map(([key, tier]) => {
            const colors = TIER_COLORS[key] || TIER_COLORS['all'];
            return (
              <div key={key} className={`p-3 border-2 ${colors.border} ${colors.bg}`}>
                <div className={`text-sm font-bold ${colors.text}`}>{tier.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{tier.grades?.join(', ')}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* School Cards */}
      <div className="space-y-3" data-testid="school-license-list">
        {schools.map(school => {
          const currentTier = getCurrentTier(school);
          const colors = TIER_COLORS[currentTier] || TIER_COLORS['all'];
          const tierInfo = tiers[currentTier] || {};
          const isChanged = pendingChanges[school.school_id] !== undefined;
          const isExpanded = expandedSchool === school.school_id;

          return (
            <div
              key={school.school_id}
              className={`border-2 transition-all ${isChanged ? 'border-[#002FA7] shadow-md' : 'border-gray-200'}`}
              data-testid={`school-license-${school.school_id}`}
            >
              <div className="p-4">
                <div className="flex items-center gap-4">
                  {/* School icon */}
                  <div className={`w-12 h-12 ${colors.bg} ${colors.text} flex items-center justify-center flex-shrink-0`}>
                    <School size={24} />
                  </div>

                  {/* School info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-base">{school.name}</h3>
                      {isChanged && (
                        <span className="text-xs px-2 py-0.5 bg-[#002FA7] text-white font-semibold">
                          CHANGED
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {tierInfo.description || 'Full access'}
                    </div>
                  </div>

                  {/* Current tier badge */}
                  <div className={`px-4 py-2 ${colors.badge} font-bold text-sm flex-shrink-0`}>
                    {tierInfo.label || currentTier}
                  </div>

                  {/* Expand toggle */}
                  <button
                    onClick={() => setExpandedSchool(isExpanded ? null : school.school_id)}
                    className="p-2 hover:bg-gray-100 transition-colors"
                    data-testid={`expand-license-${school.school_id}`}
                  >
                    <ChevronDown size={18} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {/* Tier Selector (expanded) */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-gray-200" data-testid={`tier-selector-${school.school_id}`}>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Select License Package</p>
                    <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                      {Object.entries(tiers).map(([key, tier]) => {
                        const isActive = currentTier === key;
                        const tc = TIER_COLORS[key] || TIER_COLORS['all'];
                        return (
                          <button
                            key={key}
                            onClick={() => {
                              if (key === school.license_tier) {
                                // Revert to original
                                setPendingChanges(prev => {
                                  const next = { ...prev };
                                  delete next[school.school_id];
                                  return next;
                                });
                              } else {
                                setSchoolTier(school.school_id, key);
                              }
                            }}
                            className={`p-3 border-2 text-left transition-all ${
                              isActive
                                ? `${tc.border} ${tc.bg} ring-2 ring-offset-1 ring-${tc.border.replace('border-', '')}`
                                : 'border-gray-200 hover:border-gray-400 bg-white'
                            }`}
                            data-testid={`tier-${key}-${school.school_id}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className={`text-sm font-bold ${isActive ? tc.text : 'text-gray-600'}`}>
                                {tier.label}
                              </span>
                              {isActive && <Check size={16} className={tc.text} />}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {tier.grades?.join(', ')}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Floating save bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#002FA7] text-white p-4 flex items-center justify-between z-50 shadow-lg" data-testid="save-bar">
          <span className="font-semibold">
            {Object.keys(pendingChanges).length} license change(s) pending
          </span>
          <Button
            onClick={saveBulk}
            disabled={saving}
            className="bg-white text-[#002FA7] hover:bg-gray-100 h-10 px-6 font-bold"
            data-testid="save-bar-btn"
          >
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default LicenseManager;
